import { useOrders } from '@api/useOrders'
import { useQueryClient } from '@tanstack/react-query'
import type { GridApi } from 'ag-grid-community'
import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { DetailModals } from './components/Grid/DetailModals/DetailModals'
import { updateOrderInCache } from '@/api/useOrders/util'

const OrderManagementContext = createContext<OrderManagementContextType>({} as OrderManagementContextType)

type OrderManagementContextType = {
  setOrderHistoryStoreId: (storeId: string) => void
  showModal: (orderId: string) => void
  selectedOrderId: string | undefined
  setSelectedOrderId: (orderId: string | undefined) => void
  setAuditOrderId: (orderId: string | undefined) => void
  gridAPIRef: React.MutableRefObject<GridApi>
  triggerTPDModal: (orderId: string, carrier?: string) => void
  handleTPDDismissModal: (order?: Gravitate.Order, shouldUpdate?: boolean, selectedCarrier?: string) => void
  handleOrderChange: (newOrder: Gravitate.Order) => void
}

type OrderManagementContextProviderProps = {
  children: React.ReactNode
}

export function OrderManagementContextProvider({ children }: OrderManagementContextProviderProps) {
  const [showingModal, setShowingModal] = useState()
  const [selectedOrderId, setSelectedOrderId] = useState<Gravitate.Order['id']>()
  const [showingTPDModal, setShowingTPDModal] = useState(false)
  const [orderHistoryStoreId, setOrderHistoryStoreId] = useState()
  const [auditOrderId, setAuditOrderId] = useState()
  const { useOrderByIdQuery } = useOrders()
  const { data: orderData } = useOrderByIdQuery(selectedOrderId)
  const queryClient = useQueryClient()
  const handleOrderChange = useCallback((newOrder) => {
    queryClient.setQueriesData({ queryKey: ['getOrdersQuery'] }, (oldData) => {
      return {
        ...oldData,
        rows: oldData.rows.map((order) => {
          if (newOrder.id === order.id) {
            return { ...order, ...newOrder }
          }
          return order
        }),
      }
    })
  }, [])
  const gridAPIRef = useRef()
  const closeOrderDrawer = () => setOrderHistoryStoreId(undefined)
  const closeAuditDrawer = () => setAuditOrderId(undefined)

  const showModal = (orderId) => {
    setSelectedOrderId(orderId)
    setShowingModal(true)
  }
  const closeModal = (needsReload, newOrder) => {
    setShowingModal(false)
    setSelectedOrderId(undefined)
    if (newOrder) {
      handleOrderChange(newOrder)
    }
    if (needsReload) {
      queryClient.invalidateQueries({ queryKey: ['getOrdersQuery'] }).then(() => {
        gridAPIRef?.current.redrawRows()
      })
    }
  }

  async function triggerTPDModal(orderId: Gravitate.Order['id']) {
    setShowingTPDModal(true)
    setSelectedOrderId(orderId)
  }

  function handleTPDDismissModal(updatedOrder: Gravitate.Order, shouldUpdate: boolean, carrier: string) {
    setSelectedOrderId(undefined)
    setShowingTPDModal(false)

    if (shouldUpdate) {
      updateOrderInCache({
        queryClient,
        endpointKey: 'getOrdersQuery',
        updatedOrder: { ...updatedOrder, carrier },
      })
    }
  }

  return (
    <OrderManagementContext.Provider
      value={{
        setOrderHistoryStoreId,
        showModal,
        selectedOrderId,
        setSelectedOrderId,
        setAuditOrderId,
        gridAPIRef,
        triggerTPDModal,
        handleTPDDismissModal,
        handleOrderChange,
      }}
    >
      <DetailModals
        showModal={showModal}
        orderHistoryStoreId={orderHistoryStoreId}
        closeOrderDrawer={closeOrderDrawer}
        tpdOrder={orderData}
        showingTPDModal={showingTPDModal}
        handleTPDDismissModal={handleTPDDismissModal}
        auditOrderId={auditOrderId}
        closeAuditDrawer={closeAuditDrawer}
        showingModal={showingModal}
        closeModal={closeModal}
        selectedOrderId={selectedOrderId}
        gridAPIRef={gridAPIRef}
      />
      {children}
    </OrderManagementContext.Provider>
  )
}

export const useOrderManagementContext = () => {
  const context = useContext(OrderManagementContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
